import { useEffect, useMemo, useState } from "react";
import { fetchSalesOrders, type SalesOrderSummary } from "../../lib/sales-orders";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type InvoiceFilter = "all" | "unpaid" | "partial" | "paid";

function invoiceState(order: Pick<SalesOrderSummary, "total" | "amountPaid" | "balance">) {
  if (order.total > 0 && order.amountPaid >= order.total) {
    return "paid";
  }
  if (order.amountPaid > 0) {
    return "partial";
  }
  return "unpaid";
}

function invoiceTone(state: ReturnType<typeof invoiceState>) {
  if (state === "paid") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
  }
  if (state === "partial") {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
  }
  return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300";
}

function invoiceLabel(state: ReturnType<typeof invoiceState>) {
  if (state === "paid") {
    return "Paid";
  }
  if (state === "partial") {
    return "Part paid";
  }
  return "Unpaid";
}

export function SalesInvoicesPage() {
  const [orders, setOrders] = useState<SalesOrderSummary[]>([]);
  const [query, setQuery] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>("unpaid");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalOrders: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const summary = useMemo(() => {
    const unpaid = orders.filter((order) => invoiceState(order) === "unpaid");
    const partial = orders.filter((order) => invoiceState(order) === "partial");
    const paid = orders.filter((order) => invoiceState(order) === "paid");

    return {
      visible: orders.length,
      unpaidValue: unpaid.reduce((sum, order) => sum + order.balance, 0),
      partialCount: partial.length,
      paidCount: paid.length
    };
  }, [orders]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("status", "all");
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchSalesOrders(params);
        let nextOrders = result.orders;
        if (invoiceFilter !== "all") {
          nextOrders = nextOrders.filter((order) => invoiceState(order) === invoiceFilter);
        }

        if (!ignore) {
          setOrders(nextOrders);
          setPagination(result.pagination);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load invoices.");
          setOrders([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [invoiceFilter, page, query]);

  useEffect(() => {
    setPage(1);
  }, [invoiceFilter, query]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales invoices</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Invoice desk</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">
          Find order-backed invoices quickly, check payment position, and open a clean printable invoice for customers and follow-up.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Visible invoices</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.visible}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Outstanding value</p>
          <p className="mt-3 text-2xl font-semibold leading-tight text-nv-ink">{formatCurrency(summary.unpaidValue)}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Part paid</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.partialCount}</p>
        </div>
        <div className="admin-card rounded-[28px] p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Paid</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{summary.paidCount}</p>
        </div>
      </div>

      <div className="admin-card rounded-[28px] p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px] xl:grid-cols-[minmax(0,1fr)_220px_220px]">
          <input
            className="field-control px-4 py-3 text-sm"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by order, customer, phone, address, or tracking reference"
            value={query}
          />
          <select className="field-control px-4 py-3 text-sm" onChange={(event) => setInvoiceFilter(event.target.value as InvoiceFilter)} value={invoiceFilter}>
            <option value="all">All invoices</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Part paid</option>
            <option value="paid">Paid</option>
          </select>
          <div className="field-soft rounded-[22px] p-4 text-sm text-nv-slate">
            Open any row to print or download the invoice.
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

      <div className="admin-card overflow-hidden rounded-[32px]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px]">
            <thead>
              <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                <th className="px-5 py-4">Invoice</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">State</th>
                <th className="px-5 py-4">Amounts</th>
                <th className="px-5 py-4">Reference</th>
                <th className="px-5 py-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>Loading invoices...</td></tr>
              ) : orders.length > 0 ? (
                orders.map((order) => {
                  const state = invoiceState(order);
                  return (
                    <tr className="border-b border-black/5 align-top" key={order.id}>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-nv-ink">Invoice #{order.id}</p>
                        <p className="mt-1 text-xs text-nv-slate">{formatDateTime(order.addedOn)}</p>
                        <p className="mt-2 text-sm text-nv-slate">{order.itemCount} items</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-nv-slate">
                        <p className="font-semibold text-nv-ink">{order.deliveryName || order.email || "Walk-in / unknown"}</p>
                        <p className="mt-1">{order.deliveryPhone || order.buyerPhone || "No phone"}</p>
                        <p className="mt-1">{order.email || "No email"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${invoiceTone(state)}`}>
                          {invoiceLabel(state)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-nv-slate">
                        <p className="font-semibold text-nv-ink">Total {formatCurrency(order.total)}</p>
                        <p className="mt-1">Paid {formatCurrency(order.amountPaid)}</p>
                        <p className="mt-1 font-semibold">Balance {formatCurrency(order.balance)}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-nv-slate">
                        <p>{order.trackingNumber || "Not assigned"}</p>
                        <p className="mt-1">{order.paymentType || "Payment type not recorded"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <a
                          className="inline-flex rounded-full border border-black/10 px-4 py-2 text-sm font-semibold text-nv-ink dark:border-white/10"
                          href={`/sales/invoice?order=${order.id}`}
                          rel="noreferrer"
                          target="_blank"
                        >
                          Open invoice
                        </a>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>No invoices matched the current view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
          <p>Page {pagination.page} of {pagination.totalPages} · {pagination.totalOrders} orders</p>
          <div className="flex items-center gap-2">
            <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
            <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
